/**
 * User-coupon (direct assignment) query hooks using React Relay.
 *
 * Lists a coupon's `UserCoupon` grants filtered by `couponOldId`, loading the
 * granted `user` plus audit/usage fields, for the coupon assignments admin UI.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries to
 * regenerate the type artifacts.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { fetchQuery } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type {
  useUserCouponsByCouponQuery as UseUserCouponsByCouponQuery,
  useUserCouponsByCouponQuery$variables,
  UserCouponFilterInput,
} from './__generated__/useUserCouponsByCouponQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

const UserCouponsByCouponQuery = graphql`
  query useUserCouponsByCouponQuery(
    $first: Int
    $after: String
    $filter: UserCouponFilterInput
  ) {
    listUserCoupons(first: $first, after: $after, filter: $filter) {
      count
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      edges {
        cursor
        node {
          id
          assignedBy
          couponOldId
          userOldId
          userId
          created
          timesUsed
          removedOn
          user {
            id
            phone
            email
            firstName
            lastName
            fullName
          }
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

export type { UserCouponFilterInput };

/**
 * Three-state status filter for user-coupon grants:
 *   - `ACTIVE`  -> only non-revoked grants (`removedOn` IS NULL)
 *   - `REVOKED` -> only revoked grants (`removedOn` IS NOT NULL)
 *   - `ALL`     -> no `removedOn` filter (both active and revoked)
 */
export type UserCouponStatusFilter = 'ACTIVE' | 'REVOKED' | 'ALL';

export interface UserCouponUserGraphQL {
  id: string;
  phone?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
}

export interface UserCouponGraphQL {
  id: string;
  assignedBy?: string | null;
  couponOldId: string;
  userOldId: string;
  userId?: string | null;
  created?: string | null;
  timesUsed?: number | null;
  removedOn?: string | null;
  user?: UserCouponUserGraphQL | null;
}

export interface UserCouponsByCouponFilter {
  /** The coupon's legacy `oldId` to list grants for. */
  couponOldId: string;
  /**
   * Which grants to return by revoke-status. Defaults to `ACTIVE`.
   *   - `ACTIVE`  -> non-revoked grants only
   *   - `REVOKED` -> revoked grants only
   *   - `ALL`     -> both
   */
  status?: UserCouponStatusFilter;
  /**
   * @deprecated Use `status` instead. Retained for backwards compatibility:
   * `activeOnly: false` is equivalent to `status: 'ALL'`.
   */
  activeOnly?: boolean;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * React Query-like hook (no Suspense) for listing a coupon's user-coupon
 * grants. Provides `data`, `isLoading`, `refetch`, etc.
 */
export function useUserCouponsByCoupon(
  filter: UserCouponsByCouponFilter | undefined,
  options?: { limit?: number; enabled?: boolean }
) {
  const [data, setData] = useState<UserCouponGraphQL[] | null>(null);
  const [count, setCount] = useState<number | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const limit = options?.limit ?? 100;
  const enabled = options?.enabled !== false && Boolean(filter?.couponOldId);

  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);

  useEffect(() => {
    if (!enabled || !filter?.couponOldId) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        if (isFetched) {
          setIsRefetching(true);
        } else {
          setIsLoading(true);
        }

        const variables: useUserCouponsByCouponQuery$variables = {
          first: limit,
          filter: buildRelayFilter(filter),
        };

        const result = await fetchQuery<UseUserCouponsByCouponQuery>(
          relayEnvironment,
          UserCouponsByCouponQuery,
          variables,
          { fetchPolicy: 'network-only' }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const nodes =
            result.listUserCoupons?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(nodes as UserCouponGraphQL[]);
          setCount(result.listUserCoupons?.count ?? undefined);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error('Failed to fetch user coupons'));
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
          setIsRefetching(false);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, filterKey, limit, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return {
    data,
    count,
    isLoading,
    isFetched,
    isRefetching,
    error,
    refetch,
  };
}

// ============================================================================
// Helpers
// ============================================================================

function buildRelayFilter(
  filter?: UserCouponsByCouponFilter
): UserCouponFilterInput | undefined {
  if (!filter?.couponOldId) return undefined;

  const relayFilter: UserCouponFilterInput = {
    couponOldId: { eq: filter.couponOldId },
  };

  // Resolve the effective status. `status` takes precedence; fall back to the
  // legacy `activeOnly` flag, defaulting to ACTIVE-only grants.
  const status: UserCouponStatusFilter =
    filter.status ?? (filter.activeOnly === false ? 'ALL' : 'ACTIVE');

  if (status === 'ACTIVE') {
    relayFilter.removedOn = { isNil: true };
  } else if (status === 'REVOKED') {
    relayFilter.removedOn = { isNil: false };
  }
  // status === 'ALL' -> leave `removedOn` unset (no filter).

  return relayFilter;
}
